/**
 * Projects (lots L1, L3). Mirrors backend app/schemas/projects/project.py and
 * app/schemas/projects/workflow.py.
 */

/** Lifecycle order of the statuses (used to order groups and pills). */
export const PROJECT_STATUSES = [
  'deposited',
  'analysing',
  'analysed',
  'proposed',
  'reviewing',
  'qualified',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** owner: author or org admin (edits); reader: reads and comments only. */
export type ProjectAccess = 'owner' | 'reader';
/** review: the projects the caller was asked to review. */
export type ProjectScope = 'mine' | 'gallery' | 'all' | 'review';
/** new: a new version with only the sent files; add: into the current version. */
export type DepositMode = 'new' | 'add';

export interface PersonRef {
  id: string;
  name?: string | null;
  email?: string | null;
}

/** Set once the owner proposed the project (lot L3). */
export interface ProjectProposal {
  submitted_at: string;
  submitted_by?: PersonRef | null;
  answers: Record<string, string>;
}

export interface Project {
  id: string;
  organization_id: string;
  domain_id: string;
  domain_name?: string | null;
  title: string;
  brief?: string | null;
  purpose?: string | null;
  status: ProjectStatus;
  author?: PersonRef | null;
  published_at?: string | null;
  current_version: number;
  file_count: number;
  main_file?: string | null;
  review_count: number;
  deleted_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  access: ProjectAccess;
  proposal?: ProjectProposal | null;
}

export interface ProjectCreatePayload {
  domain_id: string;
  title: string;
  brief?: string | null;
  purpose?: string | null;
}

export interface ProjectUpdatePayload {
  domain_id?: string;
  title?: string;
  brief?: string | null;
  purpose?: string | null;
}

export interface ProjectListParams {
  scope?: ProjectScope;
  domain_id?: string;
  status?: ProjectStatus;
}

export type ProjectFileKind = 'archive' | 'entry' | 'file';
export type ProjectPlayable = 'html' | 'markdown' | 'text' | 'react' | 'pdf' | 'image';

export interface ProjectFile {
  document_id: number;
  version: number;
  path: string;
  /** archive: a kept zip; entry: unpacked from a zip; file: deposited as is. */
  kind: ProjectFileKind;
  source?: string | null;
  is_main: boolean;
  playable?: ProjectPlayable | null;
  size: number;
  mime_type: string;
  sha256?: string | null;
  text_chars?: number | null;
  indexing_status?: string | null;
}

export interface ProjectVersion {
  version: number;
  deposited_at: string;
  by?: PersonRef | null;
  note?: string | null;
  file_count: number;
}

export interface DepositResult {
  version: number;
  files: ProjectFile[];
  indexing_started: number;
}

export interface ProjectReview {
  id: string;
  project_id: string;
  author?: PersonRef | null;
  /** null once deleted (the moderation trace is kept). */
  text: string | null;
  created_at: string;
  updated_at?: string | null;
  deleted: boolean;
  deleted_by_moderator: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

/* -------------------------------- workflow -------------------------------- */

/** The steps a qualifier can move a project to: review, qualify, or review again. */
export type DecisionStatus = Extract<ProjectStatus, 'reviewing' | 'qualified'>;

/** One step of the lifecycle history (oldest first; « analysing » never appears). */
export interface StatusChange {
  status: ProjectStatus;
  at: string;
  by?: PersonRef | null;
}

export interface ReviewRequest {
  id: string;
  project_id: string;
  to: PersonRef;
  requested_by?: PersonRef | null;
  message?: string | null;
  created_at: string;
  /** The recipient commented the project since being asked. */
  answered: boolean;
}

/** A request addressed to the caller (GET /projects/review-requests). */
export interface MyReviewRequest extends ReviewRequest {
  project_title: string;
  project_status: ProjectStatus;
}

export interface ReviewRequestCreatePayload {
  user_ids: string[];
  message?: string | null;
}
